import { useCallback, useMemo, useState } from 'react';
import { DomAttribute } from 'targus';
import { LoadingListItemGroup } from '../../../../../common/components/LoadingListItem/LoadingListItemGroup/LoadingListItemGroup';
import { CountryState } from '../../../../../common/components/SearchHeader/constants';
import {
  LocationChoice,
  StateChoice,
} from '../../../../../common/components/SearchHeader/types/LocationChoice';
import { useArrowKeyIndex } from '../../../../../common/hooks/keyboard/useArrowKeyIndex';
import { useEnterKeyHandler } from '../../../../../common/hooks/keyboard/useEnterKeyHandler';
import { useHannibalQuery } from '../../../../../common/schema/hannibal/HannibalContext';
import { SuburbsByNameSearchDocument } from '../../../../../common/schema/hannibal/types.generated';
import { useMobile } from '../../../../../common/services/screenSize/ScreenSizeService';
import { EMPTY_LIST } from '../../../../../common/util/EMPTY_LIST';
import { parseToEnum } from '../../../../../common/util/parseToEnum';
import { safeMemo } from '../../../../../common/util/safeMemo';
import {
  Error,
  FilterTextInput,
  Link,
  LoadingWrapper,
  LocationFilterBox,
  LocationFilterHeader,
  LocationList,
  Title,
} from '../LocationFilter.styles';
import { LocationFilterItem } from './LocationFilterItem';

export interface Props {
  value?: LocationChoice;
  onChange?: Setter<LocationChoice>;
  search?: string;
}

const STATE_CHOICES: StateChoice[] = [
  { state: CountryState.WA },
  { state: CountryState.ACT },
  { state: CountryState.NT },
  { state: CountryState.QLD },
  { state: CountryState.SA },
  { state: CountryState.TAS },
  { state: CountryState.VIC },
  { state: CountryState.NSW },
];

type Display = 'loading' | 'data' | 'error' | 'noResults';

export const LocationFilterContent = safeMemo(
  ({ onChange: onLocationSelected, value, search }: Props) => {
    const [query, setQuery] = useState(value?.suburb?.name || '');

    const { data, loading, error, refetch, variables } = useHannibalQuery(
      SuburbsByNameSearchDocument,
      {
        variables: { query },
        skip: !query,
      },
    );

    const handleTextChange: DomAttribute<'input', 'onChange'> = e => {
      setQuery(e.currentTarget.value);
    };

    const suburbs = data?.searchSuburbsByName.suburbs ?? EMPTY_LIST;

    const suburbChoices = useMemo(
      () => [
        ...suburbs.map(it => ({
          suburb: {
            name: it.name ?? undefined,
            state: parseToEnum(it.state, CountryState) ?? undefined,
            postcode: it.postcode ?? undefined,
          },
        })),
      ],
      [suburbs],
    );

    const choices: LocationChoice[] = query === '' ? STATE_CHOICES : suburbChoices;

    const [selected, setSelected] = useArrowKeyIndex(choices.length);

    const choose = useCallback(() => {
      const choice = choices[selected];
      if (choice) onLocationSelected?.(choice);
    }, [choices, onLocationSelected, selected]);

    useEnterKeyHandler(choose);

    const isMobile = useMobile();

    const display = getDisplay(choices, loading, error);

    return (
      <LocationFilterBox>
        <LocationFilterHeader>
          <Title>Location</Title>
          {/* We acknowledge that using autofocus here may potentially reduce usability and accessibility. */}
          {/* eslint-disable jsx-a11y/no-autofocus */}
          <FilterTextInput
            onChange={handleTextChange}
            value={query}
            autoFocus={!isMobile}
            placeholder="Enter a suburb or state"
          />
          {/* eslint-enable jsx-a11y/no-autofocus */}
        </LocationFilterHeader>

        {display === 'error' && (
          <Error>
            <p>
              <b>Oops!</b>
            </p>

            <p>
              Something went wrong. <Link onClick={() => refetch()}>Try again</Link>
            </p>
          </Error>
        )}

        {display === 'noResults' && (
          <Error>
            <p>
              Sorry, there are no results {search ? `for "${search}"` : ''} in{' '}
              <b>{variables?.query}</b>.
            </p>

            <p>Try another location.</p>
          </Error>
        )}

        {display === 'loading' && (
          <LoadingWrapper>
            <LoadingListItemGroup />
          </LoadingWrapper>
        )}

        {display === 'data' && (
          <LocationList>
            {choices.map((choice, index) => (
              <LocationFilterItem
                key={index}
                onChoose={choose}
                onSelect={setSelected}
                choice={choice}
                index={index}
                selected={index === selected}
              />
            ))}
          </LocationList>
        )}
      </LocationFilterBox>
    );
  },
);

const getDisplay = (choices: LocationChoice[], loading: boolean, error: unknown): Display => {
  if (choices.length) return 'data';
  if (loading) return 'loading';
  return error ? 'error' : 'noResults';
};
